/**
 * useQSLRecords Hook
 * 
 * Hook for managing QSL records data fetching and state.
 */

import * as React from "react";
import { qslService, type QSLRecord, type QSLRecordFilters } from "@/services/qslService";
import { toast } from "sonner";

export function useQSLRecords(userId?: string, filters?: QSLRecordFilters) {
  const [records, setRecords] = React.useState<QSLRecord[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [total, setTotal] = React.useState(0);

  const fetchRecords = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let result;
      if (userId) {
        result = await qslService.getUserQSLRecords(userId, filters);
      } else {
        result = await qslService.searchQSLRecords(filters);
      }
      
      setRecords(result.data);
      setTotal(result.total);
    } catch (err) {
      console.error("Error fetching QSL records:", err);
      setError(err instanceof Error ? err.message : "Gagal memuat data QSL");
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [userId, filters]);

  React.useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  return {
    records,
    loading,
    error,
    total,
    fetchRecords,
  };
}

export function useCreateQSLRecord() {
  const [creating, setCreating] = React.useState(false);

  const createQSLRecord = React.useCallback(async (
    userId: string,
    targetCallsign: string,
    qslDate?: string
  ) => {
    setCreating(true);
    try {
      const result = await qslService.createQSLRecord({
        userId,
        targetCallsign,
        qslDate,
      });
      toast.success("QSL record berhasil dibuat", {
        description: `QSL dengan ${targetCallsign} telah dicatat`,
      });
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal membuat QSL record";
      toast.error("Gagal membuat QSL record", {
        description: errorMessage,
      });
      throw err;
    } finally {
      setCreating(false);
    }
  }, []);

  return {
    createQSLRecord,
    creating,
  };
}

